<template>
  <div class="edit-contact popup-body">
    <button v-if="isNew" class="contact-type" @click="contactType = 'company'" :class="{ selected: contactType == 'company' }"><img src="../assets/company.svg" />Company</button>
    <button v-if="isNew" class="contact-type" @click="contactType = 'individual'" :class="{ selected: contactType == 'individual' }"><img src="../assets/individual.svg" />Individual</button>
    <span v-if="isNew" style="flex: 1 0 40%"></span>

    <div :class="{ 'primary-input': true, 'input-item': true, 'medium-item': true, invalid: showErrors && !contact.NAME }" v-if="contactType == 'company'">
      <input type="text" v-model="contact.NAME" />
      <label>Name</label>
    </div>

    <div :class="{ 'primary-input': true, 'input-item': true, 'medium-item': true, invalid: showErrors && !contact.FIRSTNAME }" v-if="contactType == 'individual'">
      <input type="text" v-model="contact.FIRSTNAME" />
      <label>First Name</label>
    </div>

    <div :class="{ 'primary-input': true, 'input-item': true, 'medium-item': true, invalid: showErrors && !contact.LASTNAME }" v-if="contactType == 'individual'">
      <input type="text" v-model="contact.LASTNAME" />
      <label>Last Name</label>
    </div>

    <SearchMenu
      :options="companyMatches"
      placeholder="Select Company..."
      v-if="contactType == 'individual' && isNew"
      v-model="companySearchText"
      class="input-item medium-item"
      @updateContact="assignCompany"
      @forceOptionsUpdate="companySearchTextWithDelay = companySearchText"
      @blurred="!contact.COMPANYID ? (companySearchText = '') : null" />

    <div class="primary-input input-item medium-item">
      <input type="text" v-model="contact.address.LINE1" />
      <label>Street</label>
    </div>

    <div class="primary-input input-item medium-item">
      <input type="text" v-model="contact.address.CITY" />
      <label>City</label>
    </div>

    <Dropdown label="State" :options="states" v-model="contact.address.STATE" class="input-item medium-item" />

    <div class="primary-input input-item medium-item">
      <input type="text" v-model="contact.address.POSTALCODE" />
      <label>Zip Code</label>
    </div>

    <div class="primary-input input-item medium-item">
      <input type="text" ref="phone" v-model="contact.phone.NUMBERDISPLAY" @keyup="formatPhone()" />
      <label>Phone</label>
    </div>

    <div class="primary-input input-item medium-item" v-if="contactType == 'individual'">
      <input type="text" v-model="contact.email.ADDRESS" />
      <label>Email</label>
    </div>
  </div>
  <div class="popup-footer">
    <button @click="$emit('back')" class="text-btn">back</button>
    <button :disabled="isSaving" :class="{ 'square-btn': true, saving: isSaving }" @click="save()">{{ isNew ? 'Create & Link' : 'Save & Link' }}<img src="../assets/spinner.svg" /></button>
  </div>
</template>

<script>
import Dropdown from '@/components/inputs/Dropdown.vue';
import SearchMenu from '@/components/inputs/SearchMenu.vue';
import useCompaniesQuery from '@/composables/useCompaniesQuery';
import useAlerts from '@/composables/useAlerts';
import useLeadMutate from '@/composables/useLeadMutate';
import useLeadsQuery from '@/composables/useLeadsQuery';
import useCompanyMutate from '@/composables/useCompanyMutate';
import useContactMutate from '@/composables/useContactMutate';
import useDecryptSymbols from '@/composables/useDecryptSymbols';
import useStatesAndCountries from '@/composables/useStatesAndCountries';

export default {
  name: 'EditContact',
  components: {
    Dropdown,
    SearchMenu,
  },
  props: ['contactInfo', 'type', 'isNew', 'leadid'],
  emits: ['back', 'resize', 'closePopup'],
  setup() {
    const { addAlert } = useAlerts();
    const { updateLead } = useLeadMutate();
    const { updateCompany, addCompany } = useCompanyMutate();
    const { updateContact, addContact } = useContactMutate();
    const { encryptSymbols } = useDecryptSymbols();
    const { stateAbbreviations } = useStatesAndCountries();

    return {
      companies: useCompaniesQuery.companies,
      addAlert,
      leads: useLeadsQuery.leads,
      updateLead,
      updateCompany,
      updateContact,
      addCompany,
      addContact,
      encryptSymbols,
      stateAbbreviations,
    };
  },
  data() {
    return {
      companySearchText: '',
      companySearchTextWithDelay: '',
      companySearchTimout: null,
      isSaving: false,
      contactType: 'company',
      showErrors: false,
      contact: {
        address: {},
        phone: {},
        email: {},
      },
    };
  },
  computed: {
    states() {
      return this.stateAbbreviations;
    },

    companyMatches() {
      const matches = [];
      if (this.companySearchTextWithDelay.length < 2) return matches;

      this.companies.forEach((company) => {
        if (company.NAME.toLowerCase().includes(this.companySearchTextWithDelay.toLowerCase())) {
          matches.push({
            text: company.NAME,
            value: company.COMPANYID,
            type: 'company',
          });
        }
      });

      const sortedMatches = matches.sort((a, b) => {
        if (a.text.toLowerCase().startsWith(this.companySearchTextWithDelay.toLowerCase())) return -1;
        if (b.text.toLowerCase().startsWith(this.companySearchTextWithDelay.toLowerCase())) return 1;
        if (a.text < b.text) return -1;
        if (a.text > b.text) return 1;
        return 0;
      });

      return sortedMatches.slice(0, 6);
    },
  },
  methods: {
    formatPhone() {
      const value = this.contact.phone?.NUMBERDISPLAY || '';
      const numbers = value.replace(/\D/g, '');
      const char = { 0: '(', 3: ') ', 6: '-' };
      let newValue = '';
      for (let i = 0; i < numbers.length; i++) {
        newValue += (char[i] || '') + numbers[i];
      }
      this.contact.phone.NUMBERDISPLAY = newValue;
    },
    assignCompany(company) {
      this.contact.COMPANYID = company?.value || null;
      this.companySearchText = company?.text || '';
    },
    verifyFields() {
      if (this.contactType == 'company' && !this.contact.NAME) {
        this.addAlert('Please fill out all required fields', 'high', 4);
        this.showErrors = true;
        return false;
      } else if (this.contactType == 'individual' && (!this.contact.FIRSTNAME || !this.contact.LASTNAME)) {
        this.addAlert('Please fill out all required fields', 'high', 4);
        this.showErrors = true;
        return false;
      }
      this.showErrors = false;
      return true;
    },
    async save() {
      if (!this.verifyFields()) return;
      this.isSaving = true;
      const selectedLead = this.leads.find((lead) => lead.LEADID === this.leadid);
      const leadToSave = {};

      if (this.isNew) {
        if (this.contactType == 'individual') {
          const contactToSave = {
            FIRSTNAME: this.contact.FIRSTNAME,
            LASTNAME: this.contact.LASTNAME,
            CATEGORY: 'Customer',
            COMPANYID: this.contact.COMPANYID,
            address: {
              LINE1: this.contact.address.LINE1,
              POSTALCODE: this.contact.address.POSTALCODE,
              STATE: this.contact.address.STATE,
              CITY: this.contact.address.CITY,
            },
            email: {
              ADDRESS: this.contact.email.ADDRESS,
            },
            phones: [
              {
                NUMBERDISPLAY: this.contact.phone.NUMBERDISPLAY,
                TYPEID: 'C3AE9586-6158-4D0D-9FAF-253CB0152F18',
              },
            ],
          };

          const newContactId = await this.addContact(this.encryptSymbols(contactToSave));
          leadToSave.CONTACTID = newContactId.addContact;
          leadToSave.COMPANYID = null;
          selectedLead.CONTACTID = newContactId.addContact;
          selectedLead.contact = {};
          selectedLead.contact.FIRSTNAME = this.contact.FIRSTNAME;
          selectedLead.LINKMETHOD = 'Created';
          selectedLead.contact.LASTNAME = this.contact.LASTNAME;
        } else if (this.contactType == 'company') {
          const companyToSave = {
            NAME: this.contact.NAME,
            CATEGORY: 'Customer',
            address: {
              LINE1: this.contact.address.LINE1,
              POSTALCODE: this.contact.address.POSTALCODE,
              STATE: this.contact.address.STATE,
              CITY: this.contact.address.CITY,
            },
            phones: [
              {
                NUMBERDISPLAY: this.contact.phone.NUMBERDISPLAY,
                TYPEID: 'C3AE9586-6158-4D0D-9FAF-253CB0152F18',
              },
            ],
          };

          const newCompanyId = await this.addCompany(this.encryptSymbols(companyToSave));
          leadToSave.COMPANYID = newCompanyId.addCompany;
          leadToSave.CONTACTID = null;
          selectedLead.COMPANYID = newCompanyId.addCompany;
          selectedLead.company = {};
          selectedLead.LINKMETHOD = 'Created';
          selectedLead.company.NAME = this.contact.NAME;
        }
      } else {
        if (this.contactType == 'individual') {
          selectedLead.CONTACTID = this.contact.CONTACTID;
          selectedLead.contact = {};
          selectedLead.contact.FIRSTNAME = this.contact.FIRSTNAME;
          selectedLead.LINKMETHOD = 'Linked';
          selectedLead.contact.LASTNAME = this.contact.LASTNAME;
          leadToSave.CONTACTID = this.contact.CONTACTID;
          leadToSave.COMPANYID = null;
          const contactToSave = {
            FIRSTNAME: this.contact.FIRSTNAME,
            LASTNAME: this.contact.LASTNAME,
            COMPANYID: this.contact.COMPANYID,
            CATEGORY: 'Customer',
            address: {
              LINE1: this.contact.address.LINE1,
              POSTALCODE: this.contact.address.POSTALCODE,
              STATE: this.contact.address.STATE,
              CITY: this.contact.address.CITY,
            },
            email: {
              ADDRESS: this.contact.email.ADDRESS,
            },
            phones: [
              {
                NUMBERDISPLAY: this.contact.phone.NUMBERDISPLAY,
                TYPEID: 'C3AE9586-6158-4D0D-9FAF-253CB0152F18',
              },
            ],
          };

          await this.updateContact(this.contact.CONTACTID, this.encryptSymbols(contactToSave));
        } else if (this.contactType == 'company') {
          selectedLead.COMPANYID = this.contact.COMPANYID;
          selectedLead.company = {};
          selectedLead.LINKMETHOD = 'Linked';
          selectedLead.company.NAME = this.contact.NAME;
          leadToSave.CONTACTID = null;
          leadToSave.COMPANYID = this.contact.COMPANYID;
          const companyToSave = {
            NAME: this.contact.NAME,
            CATEGORY: 'Customer',
            address: {
              LINE1: this.contact.address.LINE1,
              POSTALCODE: this.contact.address.POSTALCODE,
              STATE: this.contact.address.STATE,
              CITY: this.contact.address.CITY,
            },
            phones: [
              {
                NUMBERDISPLAY: this.contact.phone.NUMBERDISPLAY,
                TYPEID: 'C3AE9586-6158-4D0D-9FAF-253CB0152F18',
              },
            ],
          };

          await this.updateCompany(this.contact.COMPANYID, this.encryptSymbols(companyToSave));
        }
      }

      if (this.isNew) {
        leadToSave.LINKMETHOD = 'Created';
      } else {
        leadToSave.LINKMETHOD = 'Linked';
      }

      await this.updateLead(this.leadid, leadToSave);

      this.addAlert('Contact linked successfully.', 'low', 3);
      this.$emit('closePopup');
    },
  },
  watch: {
    companySearchText() {
      clearTimeout(this.companySearchTimout);
      this.companySearchTimout = setTimeout(() => {
        this.companySearchTextWithDelay = this.companySearchText;
      }, 500);
    },
  },
  mounted() {
    this.contactType = this.type;
    this.contact = this.contactInfo;
    this.$nextTick(() => {
      this.formatPhone();
    });
  },
};
</script>

<style scoped lang="scss">
.popup-body {
  display: flex;
  flex-direction: row;
  margin-top: 2rem !important;
  flex-wrap: wrap;
  gap: 1.5rem 1rem;

  .input-item {
    min-width: 20rem;
    flex: 1 0 45%;

    &.small-item {
      min-width: 10rem;
      flex: 1 0 20%;
    }

    &.large-item {
      min-width: 20rem;
      flex: 1 0 90%;
    }

    &.medium-item {
      min-width: 15rem;
      flex: 1 0 25%;
    }
  }

  .contact-type {
    color: black;
    background: #fcfcfc;
    border: $tableBorderColor solid 1px;
    border-radius: $borderRadius4;
    padding: 0.9rem 1rem;
    margin-right: 1rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    font-size: $fontSize16;

    img {
      margin-right: 0.8rem;
      width: 1.3rem;
    }

    &.selected {
      border: $primaryActionColor solid 1px;
      box-shadow: 0px 0px 2px 0px #2a4b7c80;

      img {
        filter: brightness(0) saturate(100%) invert(26%) sepia(62%) saturate(484%) hue-rotate(176deg) brightness(98%) contrast(101%);
      }
    }
  }
}
</style>
